import angular, { IOnChangesObject, IPromise, IQService } from 'angular';
import {
    StateParams,
    StateService,
    Transition,
    TransitionService,
} from 'angular-ui-router';
import {
    FontFamilyService,
    FontListParams,
    FontListParamsStateParamsResolver,
    FontStyleService,
    FontWeight,
    FontWidth,
    IFontFamilyModel,
    IFontStyleModel,
    IFoundryModel,
    SortOption,
} from '../../../tn.common';
import { ISeries } from '../../../tn.fonts/SeriesService';
import { PopoverService } from '../../../tn.popover/service.tn.popover';
import { UserService } from '../../../tn.user/service.tn.user';
import template from './series-page.html';

export function filterFamily(
    family: IFontFamilyModel,
    filters: FontListParams
): boolean {
    console.log('filterFamily', { family, filters });
    const slugMatch =
        filters.family_slug === undefined ||
        filters.family_slug.length === 0 ||
        filters.family_slug.includes(family.slug);
    if (!slugMatch) return false;
    const widthMatch =
        filters.width === undefined ||
        filters.width.length === 0 ||
        filters.width.some((w) => family.width.includes(w));
    if (!widthMatch) return false;
    const weightMatch =
        filters.weight === undefined ||
        filters.weight.length === 0 ||
        filters.weight.some((w) => family.weight.includes(w));
    if (!weightMatch) return false;
    const postureMatch =
        filters.posture === undefined ||
        filters.posture.length === 0 ||
        filters.posture.some((p) => family.posture.includes(p));
    if (!postureMatch) return false;
    return true;
}

export function filterStyle(
    style: IFontStyleModel,
    filters: FontListParams
): boolean {
    const widthMatch =
        filters.width === undefined ||
        filters.width.length === 0 ||
        filters.width.includes(style.width);
    if (!widthMatch) return false;
    const weightMatch =
        filters.weight === undefined ||
        filters.weight.length === 0 ||
        filters.weight.includes(style.weight);
    if (!weightMatch) return false;
    const postureMatch =
        filters.posture === undefined ||
        filters.posture.length === 0 ||
        filters.posture.includes(style.posture);
    if (!postureMatch) return false;
    return true;
}

export class SeriesPageController {
    // bindings
    filters: FontListParams = {};
    foundry?: IFoundryModel;
    series?: ISeries;

    // locals
    customText = '';
    disableWeightSort = false;
    disableWidthSort = false;
    disableSizeSort = false;
    families: IFontFamilyModel[] = [];
    filteredFamilies: IFontFamilyModel[] = [];
    filteredStyles: IFontStyleModel[] = [];
    groups: { family: IFontFamilyModel; styles: IFontStyleModel[] }[] = [];
    loaded = false;
    size = 100;
    sizeMin = 50;
    sizeMax = 150;
    sizeStep = 1;
    sortOptions = {
        size: new SortOption('family.tn_size[0]', false),
        weight: new SortOption('weight', false),
        width: new SortOption('width', true),
    };
    styles: IFontStyleModel[] = [];

    buyPopover = this.$tnPopover.popoverXS(
        angular.element('.popover-styles-add')
    );

    static $inject = [
        '$tnPopover',
        'tn.user.service',
        '$q',
        '$state',
        '$stateParams',
        '$transitions',
        'FontFamilyService',
        'FontStylePublicService',
    ];
    constructor(
        protected $tnPopover: PopoverService,
        userService: UserService,
        protected $q: IQService,
        protected $state: StateService,
        protected $stateParams: StateParams,
        protected $transitions: TransitionService,
        protected familyService: FontFamilyService,
        protected styleService: FontStyleService
    ) {
        const onAuthStateChanged = () => {
            this.onSeriesChanged();
        };
        userService.login$.subscribe(onAuthStateChanged);
        userService.logout$.subscribe(onAuthStateChanged);

        const onTransitionSuccess = (transition: Transition) => {
            const params = transition.params();
            this.onStateParamsChanged(params);
        };

        // the filters parameters are all dynamic to avoid rendering the entire
        // ui. since resolvers are not ran on dynamic transitions, catch the onSuccess
        // and update the filters.
        // we only catch transitions to the same state, since initial state loads should be
        $transitions.onSuccess(
            { to: 'home.foundry.series' },
            onTransitionSuccess
        );
    }

    onStateParamsChanged(params: Partial<StateParams>): void {
        const filters = FontListParamsStateParamsResolver(params);
        this.filters = filters;
        this.onFiltersChanged();
    }

    $onInit(): void {
        this.onStateParamsChanged(this.$stateParams);
    }

    $onChanges(changes: IOnChangesObject): void {
        if (
            changes.series &&
            changes.series.currentValue !== changes.series.previousValue
        ) {
            this.onSeriesChanged().then(() => this.onFiltersChanged());
        }
    }

    onSeriesChanged(): IPromise<void> {
        this.loaded = false;
        const series = this.series;
        const familyIds = this.series?.family;
        if (!series || !familyIds || familyIds.length === 0) {
            this.loaded = true;
            this.families = [];
            this.styles = [];
            this.filteredFamilies = [];
            this.filteredStyles = [];
            this.groups = [];
            return this.$q.resolve();
        }

        return this.familyService
            .list({ id: familyIds, page_size: 10000 })
            .then((response) => {
                const families = response.results;
                this.families = families;

                const styleIds = families.reduce<number[]>(
                    (acc, family) => [...acc, ...family.style],
                    []
                );
                return this.styleService
                    .list({ id: styleIds, page_size: 10000 })
                    .then(({ results: styles }) => {
                        this.styles = styles;
                        this.onFiltersChanged();
                    });
            })
            .finally(() => {
                this.loaded = true;
            });
    }

    onFiltersChanged(): void {
        this.filteredFamilies = this.families
            .filter((family) => this.filterFamily(family))
            .sort((a, b) => {
                // sort by recommended size, then name..
                const diff =
                    Math.max.apply(null, b.recommended_size) -
                    Math.max.apply(null, a.recommended_size);
                if (diff !== 0) return diff;

                if (a.name < b.name) return -1;
                if (b.name < a.name) return 1;
                return 0;
            });
        if (this.filteredFamilies.length === 0) {
            this.groups = [];
            this.disableSizeSort = true;
            this.disableWeightSort = true;
            this.disableWidthSort = true;
            return;
        }
        this.filteredStyles = this.styles.filter((style) =>
            this.filterStyle(style)
        );
        if (this.filteredStyles.length === 0) {
            this.groups = [];
            return;
        }
        this.groups = this.filteredFamilies.map((family) => {
            return {
                family: family,
                styles: this.filteredStyles.filter(
                    (style) => style.family === family.id
                ),
            };
        });
        const distinctSortOptions = this.filteredStyles.reduce(
            (acc, style) => {
                style.tn_size.forEach((s) => acc.sizes.add(s));
                style.weight && acc.weights.add(style.weight);
                style.width && acc.widths.add(style.width);
                return acc;
            },
            {
                sizes: new Set<number>(),
                widths: new Set<FontWidth>(),
                weights: new Set<FontWeight>(),
            }
        );
        this.disableSizeSort = distinctSortOptions.sizes.size <= 1;
        this.disableWeightSort = distinctSortOptions.weights.size <= 1;
        this.disableWidthSort = distinctSortOptions.widths.size <= 1;
    }

    filterFamily(family: IFontFamilyModel): boolean {
        return filterFamily(family, this.filters);
    }

    filterStyle(style: IFontStyleModel): boolean {
        return filterStyle(style, this.filters);
    }
}

export const SeriesPageComponent = {
    bindings: {
        series: '<',
        foundry: '<',
    },
    controller: SeriesPageController,
    controllerAs: 'vm',
    template,
};
